import {BaseRouteMetaDataModel} from "../../../infrastructure/models/route/base-route.meta-data.model";
import {computed} from "mobx";
import {EntityDescriptionSectionViewModel} from "../../../domain/entity-description/models/section/entity-description.section.view-model";
import {SPORT_TYPES} from "../../../core/app/consts";
import {WebsiteModuleState} from "../../../apps/website/models/website-module.state";
import {MetadataManager} from "../../../core/managers/metadata.manager";
import {SchemaManager} from "../../../core/managers/schema-manager";
import {BaseSchemaModel} from "../../../core/models/schema/base-schema-model";
import {SITE_DOMAINS} from "../../../core/consts";
import {HrefModel} from "../../../core/models/meta-data/href-model";
import {DEFAULT_CULTURE_NAME} from "../../../core/app/consts";
import {WEBSITE_MODULE} from "../../../apps/website/website-module.consts";
import {ARGS} from "../../../core/bootstrap/args";

export class CompetitorPageMetaDataModel extends BaseRouteMetaDataModel {
  constructor(ioc, state) {
    super(ioc);

    this.state = state
  }

  @computed get websiteModuleState() {
    const websiteModuleState: WebsiteModuleState = this.resolve(WebsiteModuleState);

    return websiteModuleState
  }

  @computed get websiteTerms() {
    return this.websiteModuleState.terms
  }

  @computed get metadataManager() {
    return this.resolve(MetadataManager);
  }

  @computed get hasSections() {
    return this.metadataManager.hasSections
  }

  @computed get schemaManager(): SchemaManager {
    return this.resolve(SchemaManager);
  }

  @computed get titleSection(): EntityDescriptionSectionViewModel {
    if(this.hasSections) {
      const titleSection = this.metadataManager.sections.first()
      const section: EntityDescriptionSectionViewModel = new EntityDescriptionSectionViewModel(titleSection);

      return section;
    }

    return null
  }

  @computed get descriptionSection() {
    if(this.hasSections) {
      const descriptionSection = this.metadataManager.sections.last()
      const section: EntityDescriptionSectionViewModel = new EntityDescriptionSectionViewModel(descriptionSection);

      return section;
    }

    return null
  }

  titleFormatTerm(filterTerm) {
    const title: String = filterTerm.format({
      COMPETITOR_NAME: this.competitorName,
      SPORT_TYPE: this.competitorSportName,
      COMPETITOR_FIXTURES_TERM_IN_TITLE: this.websiteTerms.COMPETITOR_FIXTURES_TERM_IN_TITLE,
      TABLE_AND_STANDINGS: this.websiteTerms.TABLE_AND_STANDINGS,
      COMPETITOR_RESULTS_TERM_IN_TITLE: this.websiteTerms.COMPETITOR_RESULTS_TERM_IN_TITLE,
      WEB_COMPETITOR_STATS_TERM_IN_TITLE: this.websiteTerms.WEB_COMPETITOR_STATS_TERM_IN_TITLE,
      COMPETITOR_HOME_TERM_IN_TITLE: this.websiteTerms.COMPETITOR_HOME_TERM_IN_TITLE.format({
        "LIVESCORE_MAIN_KEYWORD": this.websiteTerms.LIVESCORE_MAIN_KEYWORD
      }),
    });

    return title
  }

  descriptionFormatTerm(filterTerm) {
    const description: String = filterTerm.format({
      "LIVESCORE_MAIN_KEYWORD": this.websiteTerms.LIVESCORE_MAIN_KEYWORD,
      "COMPETITOR_NAME": this.competitorName,
      "BRACKET_NAME": this.bracketsName,
      "BRACKETS_NAME": this.bracketsName
    });

    return description
  }

  get competitorName() {
    return this.websiteModuleState.entitiesContext.competitor?.longName || this.websiteModuleState.entitiesContext.competitor?.name
  }

  get competitorSportName() {
    return this.websiteModuleState.entitiesContext.competitor?.sport?.name
  }

  get bracketsName(): String {
    const isTennis: Boolean = this.websiteModuleState.entitiesContext.competitor?.sport?.id === SPORT_TYPES.tennis

    return isTennis ? this.websiteTerms.WEB_DRAW_INSTEAD_OF_BRACKET : this.websiteTerms.WEB_BRACKETS;
  }

  get dataSchema(): Array<BaseSchemaModel> {
    if(!this.state.competitor) {
      return null;
    }

    return [this.schemaManager.createSportTeam(this.state.competitor, this.state.competitorSquad)];
  }

  get canonicalUrl(): String {
    let path: String = window.location.pathname;
    let href: String = (`${SITE_DOMAINS.full}${path}`).replace(/\?.*$/, '');

    const lastChar = href.charAt(href.length - 1);
    if (lastChar === '/') {
      return href.substring(0, href.length - 1)
    }

    return href;
  }

  get languageRepository() {
    return this.websiteModuleState.languageRepository;
  }

  get pathWithoutCulture(): String {
    let path: String = window.location.pathname;
    const cultureName: String = this.languageRepository.selectedLanguage?.displayName;

    if (cultureName && cultureName !== DEFAULT_CULTURE_NAME) {
      const culturePrefix: String = '/' + cultureName;
      if (path.startsWith(culturePrefix + '/') || path === culturePrefix) {
        path = path.substring(culturePrefix.length) || '/';
      }
    }

    return path;
  }

  get xDefaultLink(): String {
    let href: String = (`${SITE_DOMAINS.full}${this.pathWithoutCulture}`).replace(/\?.*$/, '');

    const lastChar = href.charAt(href.length - 1);
    if (lastChar === '/') {
      return href.substring(0, href.length - 1)
    }

    return href;
  }

  get alternateLinks(): Array<HrefModel> {
    const languages = this.languageRepository.languages?.filter((lang) => lang.displayName !== DEFAULT_CULTURE_NAME);

    return languages?.map((lang) => {
      let href: String = (`${SITE_DOMAINS.full}/${lang.displayName}${this.pathWithoutCulture}`).replace(/\?.*$/, '');
      const lastChar = href.charAt(href.length - 1);
      if (lastChar === '/') {
        href = href.substring(0, href.length - 1);
      }

      return new HrefModel(href, this.hrefLangName(lang));
    });
  }

  hrefLangName(lang): String {
    const cultureNameLanguages: Array<Number> = Object.values(WEBSITE_MODULE.HREF_CULTURE_NAME_LANG_ID);

    return cultureNameLanguages.includes(lang.id) ? lang.cultureName : lang.displayName;
  }

  get isStoreDomain(): Boolean {
    return this.websiteModuleState.isStoreDomain;
  }

  @computed get isNoIndex(): Boolean {
    const isHindiLanguage: Boolean = this.languageRepository.selectedLanguage.id === 216;
    const isCricket: Boolean = this.websiteModuleState.entitiesContext.competitor?.sport?.id === SPORT_TYPES.cricket;

    return ARGS.NO_INDEX === 'true' || this.websiteModuleState.entitiesContext.competitor.hide || (isHindiLanguage && !isCricket);
  }
}